import React, { useEffect, useState } from 'react';
import { BookOpen, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

const FALLBACK_ARTICLES = [
  {
    id: 1,
    title: 'Kewajiban dan Larangan Konsultan Pajak dalam PMK 55 Tahun 2026',
    date: '25/09/2026',
    category: 'Konsultasi Pajak',
    url: 'https://enforcea.com/Blog/kewajiban-dan-larangan-konsultan-pajak-dalam-pmk-55-tahun-2026',
    source: 'enforcea.com',
  },
  {
    id: 2,
    title: 'Izin Konsultan Pajak Tingkat A, B dan C: Apa Bedanya?',
    date: '25/09/2026',
    category: 'Konsultasi Pajak',
    url: 'https://enforcea.com/Blog/izin-konsultan-pajak-tingkat-a-b-dan-c-apa-bedanya',
    source: 'enforcea.com',
  },
  {
    id: 3,
    title: 'Status Pajak WNA Menentukan Kewajiban Pajaknya',
    date: '25/09/2026',
    category: 'Konsultasi Pajak',
    url: 'https://enforcea.com/Blog/status-pajak-wna-menentukan-kewajiban-pajaknya',
    source: 'enforcea.com',
  },
  {
    id: 4,
    title: 'Skema Pemungutan PPN atas Transaksi Digital Luar Negeri',
    date: '24/09/2026',
    category: 'Konsultasi Pajak',
    url: 'https://enforcea.com/Blog/skema-pemungutan-ppn-atas-transaksi-digital-luar-negeri',
    source: 'enforcea.com',
  },
];

function deduplicateArticles(items) {
  const seen = new Set();
  const result = [];
  for (const item of items) {
    const normTitle = (item.title || '').trim().toLowerCase();
    if (!normTitle || seen.has(normTitle)) continue;
    seen.add(normTitle);
    result.push(item);
  }
  return result;
}

export default function EnforceaBlogPanel() {
  const { isEnglish } = useLanguage();
  const [articles, setArticles] = useState(FALLBACK_ARTICLES);
  const [loading, setLoading] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/enforcea-articles?limit=8`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000,
      });

      if (res.data?.status === 'ok' && res.data?.data?.length > 0) {
        const mapped = res.data.data.map((item, idx) => {
          let dateStr = item.published_date || '';
          let cat = 'Artikel Pajak';
          if (dateStr.includes('•')) {
            const parts = dateStr.split('•');
            dateStr = parts[0].trim();
            cat = parts[1].trim() || cat;
          }
          return {
            id: item.id || idx + 1,
            title: item.title,
            date: dateStr,
            category: cat,
            url: item.url || 'https://enforcea.com/Blog',
            source: 'enforcea.com',
          };
        });
        const unique = deduplicateArticles(mapped).slice(0, 4);
        if (unique.length > 0) {
          setArticles(unique);
        }
      }
    } catch (err) {
      // Fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    const interval = setInterval(fetchArticles, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="gold-card p-5">
      <div className="flex items-center justify-between mb-3.5">
        <div className="flex items-center gap-2">
          <BookOpen size={13} className="text-text-sec" />
          <span className="text-[11px] uppercase font-medium tracking-wider text-text-sec">
            {isEnglish ? 'EnforceA Articles' : 'Artikel EnforceA'}
          </span>
        </div>
        <button
          onClick={fetchArticles}
          disabled={loading}
          className="p-1 rounded-md text-text-sec hover:text-text-pri hover:bg-surface-hover transition-colors"
          title={isEnglish ? 'Refresh Articles' : 'Refresh Artikel'}
        >
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="divide-y divide-ledger-border/60">
        {articles.map((item) => (
          <a
            key={item.id}
            href={item.url || 'https://enforcea.com/Blog'}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start justify-between gap-2.5 group py-3 first:pt-0 last:pb-0 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="text-xs text-text-pri group-hover:underline leading-relaxed font-normal line-clamp-2">
                {item.title}
              </p>
              <div className="flex items-center gap-1.5 mt-1 text-[10.5px] text-text-sec">
                <span className="font-mono">{item.date}</span>
                <span>·</span>
                <span>{item.source}</span>
              </div>
            </div>
            <ExternalLink
              size={11}
              className="text-muted group-hover:text-text-pri mt-1 shrink-0 transition-colors"
            />
          </a>
        ))}
      </div>

      <div className="pt-3 mt-3 border-t border-ledger-border/60">
        <a
          href="https://enforcea.com/Blog"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between text-[11px] font-medium text-text-sec hover:text-text-pri transition-colors"
          style={{ textDecoration: 'none' }}
        >
          <span>{isEnglish ? 'View all on enforcea.com' : 'Lihat semua di enforcea.com'}</span>
          <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}
